/**
 * The two non-call, non-card choices in 500 (rules §5.4, §4.3):
 *
 *  1. **Kitty discard** — the contractor takes the 3 kitty cards unseen by
 *     anyone else and puts 3 back face down, dead for the rest of the hand.
 *  2. **Joker nomination** — NT/Misère/Open Misère only, contractor only, once,
 *     before the lead to trick 1. Declining leaves the Joker floating.
 *
 * Order matters: the kitty comes first, so a misère bidder who buries the Joker
 * is never asked to nominate it (which is exactly the bot's policy, §10.2).
 */
import { SUITS } from '../../core/cards.js';
import { partnerOf } from '../../core/seats.js';
import { emptyTrick } from '../../core/trick.js';
import { FH_TRICKS_PER_HAND, kittySize } from './config.js';
import { isMisereMode } from './types.js';
export const FH_DISCARD_KITTY = 'discardKitty';
export const FH_NOMINATE_JOKER = 'nominateJoker';
/** Rules §5.4: the contractor takes the kitty for every contract type by
 *  default; Trickster's "Misère Gets Kitty" option can withhold it. */
export function takesKitty(contract, config) {
    if (!isMisereMode(contract.mode))
        return true;
    if (config.misereGetsKitty === 'always')
        return true;
    if (config.misereGetsKitty === 'never')
        return false;
    return contract.mode !== 'openMisere'; // 'unlessOpen'
}
/**
 * Rules §4.3 mode 1 — only the contractor, only when there is no trump suit to
 * attach the Joker to.
 *
 * Deliberately NOT conditional on actually holding the Joker. `pending.kind` is
 * public (only the payload is redacted), so raising the prompt only for a
 * contractor who holds it would broadcast "the contractor has the Joker" to the
 * whole table — a real information leak, and one the physical game does not
 * have, because at a table nobody asks. Every NT/misère contractor is offered
 * the declaration; the payload tells them privately whether there is anything
 * to declare.
 */
export function owesJokerNomination(contract, config) {
    if (!config.allowJokerNomination)
        return false;
    return contract.mode !== 'suit';
}
export function canNominateJoker(contract, holding, config) {
    return owesJokerNomination(contract, config) && holding.includes('JOKER');
}
export function fhInitHandExtra(seatCount) {
    return {
        jokerNominated: null,
        jokerNominationResolved: false,
        suitsLed: [],
        leadNominations: Array.from({ length: FH_TRICKS_PER_HAND }, () => null),
        revealedVoids: Array.from({ length: seatCount }, () => []),
        kittyTaken: false,
        discardResolved: false,
        openMisereExposed: false,
        sittingOut: null,
    };
}
export function discardPrompt(seat, config, kitty) {
    const n = kittySize(config);
    const payload = {
        chooseCards: n,
        from: 'holding',
        label: `Discard ${n} cards face down`,
        kitty: [...kitty],
    };
    return { seat, kind: FH_DISCARD_KITTY, payload };
}
export function nominatePrompt(seat, holdsJoker) {
    const options = holdsJoker
        ? [...SUITS.map((suit) => ({ kind: 'nominateJoker', suit })), { kind: 'nominateJoker', suit: null }]
        : [{ kind: 'nominateJoker', suit: null }];
    const payload = {
        options,
        holdsJoker,
        label: holdsJoker ? 'Nominate a suit for the Joker, or leave it floating' : 'Ready to lead',
    };
    return { seat, kind: FH_NOMINATE_JOKER, payload };
}
/**
 * Everything that happens the moment the auction produces a contract: the
 * partner sits out for a misère (rules §5.3), the contractor picks up the
 * kitty, and the first prompt is raised. The contractor leads trick 1 (§5.2).
 */
export function fhOnContract(hand, table, config) {
    const contract = hand.contract;
    if (!contract) {
        return { extra: hand.extra, pending: null, activeSeats: hand.activeSeats, leader: hand.dealer };
    }
    const contractor = contract.contractor;
    // Rules §5.3 / edge #8: in a misère the contractor's partner puts their cards
    // face down and takes no part — 3-handed tricks, still 10 of them.
    const sittingOut = isMisereMode(contract.mode) ? partnerOf(table, contractor) : null;
    const activeSeats = Array.from({ length: table.seatCount }, (_, i) => i).filter((s) => s !== sittingOut);
    const takeKitty = takesKitty(contract, config);
    const holdings = hand.secret.holdings.map((h, s) => s === contractor && takeKitty ? [...h, ...hand.secret.kitty] : [...h]);
    let extra = { ...fhInitHandExtra(table.seatCount), sittingOut, kittyTaken: takeKitty };
    if (takeKitty) {
        return { extra, pending: discardPrompt(contractor, config, hand.secret.kitty), activeSeats, holdings, leader: contractor };
    }
    extra = { ...extra, discardResolved: true };
    if (owesJokerNomination(contract, config)) {
        const pending = nominatePrompt(contractor, holdings[contractor].includes('JOKER'));
        return { extra, pending, activeSeats, holdings, leader: contractor };
    }
    return {
        extra: withOpenMisereExposed(contract, { ...extra, jokerNominationResolved: true }),
        pending: null,
        activeSeats,
        holdings,
        leader: contractor,
    };
}
function err(message) {
    return { ok: false, error: message };
}
/** Open Misère: the contractor's hand goes face up the moment play begins —
 *  after the kitty exchange, so the three buried cards stay dead and unseen
 *  (edge #10), and before the lead to trick 1 (edge #7). */
function withOpenMisereExposed(contract, extra) {
    return contract?.mode === 'openMisere' ? { ...extra, openMisereExposed: true } : extra;
}
/** Once no decision is owed the hand is ready to play; the first trick is led
 *  by the contractor (rules §5.2). */
function beginPlay(hand, extra) {
    const leader = hand.contract?.contractor ?? hand.dealer;
    return {
        ...hand,
        extra: withOpenMisereExposed(hand.contract, extra),
        pending: null,
        phase: 'play',
        tricks: hand.tricks.length > 0 ? hand.tricks : [emptyTrick(0, leader)],
    };
}
/**
 * Decisions arrive from the wire (and from the core RandomBot, which answers a
 * `chooseCards` prompt with a bare `{ cards }` and an `options` prompt with one
 * of the listed entries), so the shape is normalised against the pending
 * prompt's kind rather than trusted.
 */
function normaliseDecision(pendingKind, raw) {
    const bag = (typeof raw === 'object' && raw !== null ? raw : {});
    const kind = typeof bag.kind === 'string' ? bag.kind : pendingKind;
    if (kind !== pendingKind)
        return null;
    if (kind === FH_DISCARD_KITTY) {
        if (!Array.isArray(bag.cards))
            return null;
        return { kind: 'discardKitty', cards: bag.cards };
    }
    if (kind === FH_NOMINATE_JOKER) {
        const suit = bag.suit;
        if (suit !== null && suit !== undefined && !SUITS.includes(suit))
            return null;
        return { kind: 'nominateJoker', suit: (suit ?? null) };
    }
    return null;
}
export function fhApplyDecision(hand, seat, raw, _table, config) {
    const contract = hand.contract;
    if (!contract)
        return err('no contract yet');
    const pending = hand.pending;
    if (!pending)
        return err('no decision is pending');
    if (pending.seat !== seat)
        return err(`decision belongs to seat ${pending.seat}`);
    const decision = normaliseDecision(pending.kind, raw);
    if (!decision)
        return err(`expected a ${pending.kind} decision`);
    if (decision.kind === FH_DISCARD_KITTY) {
        const holding = hand.secret.holdings[seat] ?? [];
        const want = kittySize(config);
        const cards = decision.cards;
        if (cards.length !== want)
            return err(`discard exactly ${want} cards`);
        if (new Set(cards).size !== cards.length)
            return err('discard cards must be distinct');
        const pool = [...holding];
        for (const c of cards) {
            const at = pool.indexOf(c);
            if (at < 0)
                return err(`${c} is not in your hand`);
            pool.splice(at, 1);
        }
        if (pool.length !== FH_TRICKS_PER_HAND) {
            return err(`a hand must be ${FH_TRICKS_PER_HAND} cards after the discard`);
        }
        const holdings = hand.secret.holdings.map((h, s) => (s === seat ? pool : [...h]));
        const secret = {
            ...hand.secret,
            holdings,
            // The kitty is gone: those cards are in the contractor's hand, and the
            // three going back are dead for the rest of the hand (edge #10).
            kitty: [],
            discards: [...hand.secret.discards, { seat, cards: [...cards] }],
        };
        const extra = { ...hand.extra, discardResolved: true };
        const next = { ...hand, secret, extra };
        if (owesJokerNomination(contract, config)) {
            const prompt = nominatePrompt(seat, pool.includes('JOKER'));
            return { ok: true, hand: { ...next, pending: prompt, phase: 'decision' } };
        }
        return { ok: true, hand: beginPlay(next, { ...extra, jokerNominationResolved: true }) };
    }
    // nominateJoker
    const holding = hand.secret.holdings[seat] ?? [];
    if (!owesJokerNomination(contract, config))
        return err('no Joker nomination is available');
    if (decision.suit !== null && !holding.includes('JOKER')) {
        return err('you do not hold the Joker');
    }
    const extra = {
        ...hand.extra,
        jokerNominated: decision.suit,
        jokerNominationResolved: true,
    };
    return { ok: true, hand: beginPlay(hand, extra) };
}
//# sourceMappingURL=decisions.js.map